import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Package, AlertTriangle, MapPin, Camera, CheckCircle2, X } from 'lucide-react';

const ValidateReturnsPage = () => {
    const [routes, setRoutes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedRoute, setExpandedRoute] = useState(null);
    const [validatingOrderId, setValidatingOrderId] = useState(null);

    useEffect(() => {
        fetchPendingValidations();
    }, []);

    const fetchPendingValidations = async () => {
        try {
            setIsLoading(true);
            const res = await fetch('/api/cartera/pending-validations', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                }
            });
            const data = await res.json();

            if (data.success) {
                setRoutes(data.routes);
            } else {
                toast.error('Error cargando validaciones pendientes');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error de conexión');
        } finally {
            setIsLoading(false);
        }
    };

    const handleValidateReturn = async (orderId) => {
        const confirmed = window.confirm(
            '¿Confirmas que recibiste físicamente los productos de este pedido?'
        );

        if (!confirmed) return;

        try {
            setValidatingOrderId(orderId);
            const res = await fetch(`/api/cartera/validate-return/${orderId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    physicalReturnConfirmed: true,
                    notes: 'Productos verificados y recibidos en bodega'
                })
            });

            const data = await res.json();

            if (data.success) {
                toast.success('✅ Retorno validado. Pedido enviado a Comercial para revisión.');
                // Recargar lista
                fetchPendingValidations();
            } else {
                toast.error(data.message || 'Error validando retorno');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error de conexión');
        } finally {
            setValidatingOrderId(null);
        }
    };

    const translateReason = (reason) => {
        const reasons = {
            'cliente_ausente': 'Cliente no estaba / no contestó',
            'cliente_rechazo': 'Cliente rechazó el pedido',
            'direccion_incorrecta': 'Dirección incorrecta',
            'problemas_trafico': 'Problemas de tráfico/tiempo',
            'otro': 'Otro motivo'
        };
        return reasons[reason] || reason;
    };

    const formatCurrency = (amount) => {
        return Number(amount || 0).toLocaleString('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('es-CO', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <Package className="h-8 w-8 text-orange-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            Validar Retornos Físicos
                        </h1>
                        <p className="text-gray-500 mt-1">
                            Verifica que los productos retornados estén físicamente en bodega
                        </p>
                    </div>
                </div>
            </div>

            {/* Routes List */}
            {routes.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-500" />
                    <p className="text-lg text-gray-600">
                        ✅ No hay retornos pendientes de validación
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {routes.map((route) => (
                        <div key={route.routeId || 'sin_ruta'} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                            {/* Route Header */}
                            <div
                                className="p-6 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
                                onClick={() => setExpandedRoute(
                                    expandedRoute === route.routeId ? null : route.routeId
                                )}
                            >
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">
                                            {route.routeName}
                                        </h3>
                                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                            <span>📦 Mensajero: <strong>{route.messengerName}</strong></span>
                                            {route.closedAt && (
                                                <span>🕒 Cerrada: {formatDateTime(route.closedAt)}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-3xl font-bold text-orange-600">
                                            {route.failedOrders.length}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            pedidos por validar
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Failed Orders */}
                            {expandedRoute === route.routeId && (
                                <div className="p-6 space-y-4 border-t border-gray-200">
                                    {route.failedOrders.map((order) => (
                                        <div
                                            key={order.id}
                                            className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                                        >
                                            {/* Order Header */}
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h4 className="text-lg font-bold text-gray-900">
                                                        {order.orderNumber}
                                                    </h4>
                                                    <p className="text-gray-600">{order.customerName}</p>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        Valor: {formatCurrency(order.totalAmount)}
                                                    </p>
                                                </div>

                                                {order.isBadlyRouted && (
                                                    <div className="bg-orange-100 border border-orange-400 rounded-lg px-3 py-2">
                                                        <div className="flex items-center gap-2 text-orange-700">
                                                            <AlertTriangle className="h-4 w-4" />
                                                            <span className="text-sm font-medium">Mal Enrutado</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Failure Reason */}
                                            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                                                <p className="font-medium text-red-900">
                                                    Razón de No Entrega:
                                                </p>
                                                <p className="text-lg text-red-800 mt-1">
                                                    {translateReason(order.failureReason)}
                                                </p>
                                                {order.failureNotes && (
                                                    <p className="mt-2 text-sm text-red-700 italic">
                                                        "{order.failureNotes}"
                                                    </p>
                                                )}
                                            </div>

                                            {/* Evidence Photo */}
                                            {order.photoFilename && (
                                                <div className="mb-4">
                                                    <p className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                                                        <Camera className="h-4 w-4" />
                                                        Evidencia Fotográfica:
                                                    </p>
                                                    <img
                                                        src={`/uploads/evidence/delivery/${order.photoFilename}`}
                                                        alt="Evidencia de entrega fallida"
                                                        className="w-64 h-64 object-cover rounded-lg border-2 border-gray-300 cursor-pointer hover:scale-105 transition"
                                                        onClick={() => window.open(`/uploads/evidence/delivery/${order.photoFilename}`, '_blank')}
                                                    />
                                                </div>
                                            )}

                                            {/* Corrected Address */}
                                            {order.correctedAddress && (
                                                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                                                    <p className="font-medium text-blue-900 flex items-center gap-2">
                                                        <MapPin className="h-4 w-4" />
                                                        Dirección Corregida:
                                                    </p>
                                                    <p className="text-blue-800 mt-1">{order.correctedAddress}</p>
                                                    {order.correctedLatitude && order.correctedLongitude && (
                                                        <a
                                                            href={`https://maps.google.com/?q=${order.correctedLatitude},${order.correctedLongitude}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 underline text-sm mt-2 inline-block"
                                                        >
                                                            Ver en Google Maps →
                                                        </a>
                                                    )}
                                                    {order.addressCorrectionAction && (
                                                        <p className="text-sm text-blue-700 mt-2">
                                                            Acción sugerida: <strong>{order.addressCorrectionAction === 're_route' ? 'Reenrutar' : 'Cancelar'}</strong>
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Action Button */}
                                            <div className="flex justify-end pt-4 border-t border-gray-300">
                                                <button
                                                    onClick={() => handleValidateReturn(order.id)}
                                                    disabled={validatingOrderId === order.id}
                                                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 active:scale-95 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {validatingOrderId === order.id ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                            Validando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CheckCircle2 className="h-5 w-5" />
                                                            Validar Recepción Física
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ValidateReturnsPage;
